import { useContext, useEffect, useMemo } from "react";
import { SERVICENOW_CHAT_INSTANCE, SERVICENOW_CHAT_MODULE_ID } from "../../Config/Environment";
import { FixtureDetailsContext } from "../../Config/FixtureDetails";

// Script version from the embed snippet provided by the ServiceNow team (SER-10826).
const SCRIPT_VERSION = "5.6";
const SCRIPT_ID = "ServiceNow-chat-sdk";

// The messenger is a page-level singleton, so its state is kept outside React.
let scriptPromise = null;
let initializedContextKey = null;

const loadScript = () => {
  if (window.SN_CSM_EC) {
    return Promise.resolve(window.SN_CSM_EC);
  }
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.async = true;
      script.src = `${SERVICENOW_CHAT_INSTANCE}/scripts/sn_csm_ec.js?v=${SCRIPT_VERSION}`;
      script.addEventListener("load", () => resolve(window.SN_CSM_EC));
      script.addEventListener("error", (error) => {
        // Allow a later mount to retry instead of waiting on a script that already failed.
        script.remove();
        scriptPromise = null;
        reject(error);
      });
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
};

const destroyMessenger = () => {
  if (window.SN_CSM_EC && initializedContextKey) {
    initializedContextKey = null;
    window.SN_CSM_EC.destroy();
  }
};

const competitorName = (competitor) => competitor?.nameFullLocal || competitor?.fullNameLatin;

const buildMatchup = (competitors) => {
  const home = competitors?.find((competitor) => competitor.isHome);
  const away = competitors?.find((competitor) => !competitor.isHome);
  return [competitorName(home), competitorName(away)].filter(Boolean).join(" vs ");
};

const ServiceNowChat = ({ currentState }) => {
  const { fixtureId, sport, competitors, hasLoaded } = useContext(FixtureDetailsContext);
  const sessionInfo = currentState?.sessionInfo;
  const isLoggedIn = Boolean(currentState?.authKey && sessionInfo);
  const username = sessionInfo?.username || "";
  const scoutId = String(sessionInfo?.userId || "");
  const matchup = useMemo(() => buildMatchup(competitors), [competitors]);

  // Only the scout and the fixture re-initialize the messenger, so detail refreshes don't end an open chat.
  const contextKey = isLoggedIn && fixtureId && hasLoaded ? JSON.stringify([username, scoutId, fixtureId]) : null;

  useEffect(() => {
    if (!isLoggedIn) {
      destroyMessenger();
      return;
    }
    if (!contextKey) {
      return;
    }
    let cancelled = false;
    loadScript()
      .then((messenger) => {
        if (cancelled) {
          return;
        }
        if (initializedContextKey === contextKey) {
          messenger.show();
          return;
        }
        destroyMessenger();
        messenger.init({
          moduleID: `${SERVICENOW_CHAT_INSTANCE}/#${SERVICENOW_CHAT_MODULE_ID}`,
          loadFeature: {
            feature: "CHAT",
            openOnLoad: false,
            params: {
              sysparm_username: username,
              sysparm_scout_id: scoutId,
              sysparm_sport: sport || "",
              sysparm_match_id: String(fixtureId),
              sysparm_matchup: matchup,
              sysparm_source_portal: window.location.hostname,
            },
          },
        });
        initializedContextKey = contextKey;
      })
      .catch((error) => console.error("ServiceNow chat: initialization failed.", error));
    return () => {
      cancelled = true;
    };
  }, [contextKey, isLoggedIn]);

  useEffect(() => {
    return () => {
      if (window.SN_CSM_EC && initializedContextKey) {
        window.SN_CSM_EC.hide();
      }
    };
  }, []);

  return <></>;
};

export default ServiceNowChat;
